/**
 * App.tsx — Amplifyr: Personal Intelligence Amplification Platform
 *
 * Tabbed workspace: Documents (expressr multi-context synthesis) + Chat (Ember)
 * Uses direct React rendering to avoid appr-ui → actr → aggregatr Node-only chain.
 */

import { useState } from 'react';
import { ExpressrProvider, ExpressrWorkspace } from '@opcentral/expressr-ui';
import { ReversrProvider } from '@opcentral/reversr-ui';
import type { LLMProvider, LLMResult } from '@opcentral/expressr-lib';
import type { ExpressrUIConfig } from '@opcentral/expressr-ui';
import { GovernancePanel } from './components/GovernancePanel.js';
import { ChatView } from './views/ChatView.js';
import { ReachrView } from './views/ReachrView.js';
import { DataView } from './views/DataView.js';
import { AppsView } from './views/AppsView.js';
import { SchedulingView } from './views/SchedulingView.js';

// ── LLM Provider — calls server-side synthesis endpoint ──

const llmProvider: LLMProvider = {
  async generate(
    prompt: string,
    options?: { temperature?: number; maxTokens?: number; personality?: string },
  ): Promise<LLMResult> {
    const response = await fetch('/api/synthesis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        temperature: options?.temperature ?? 0.5,
        maxTokens: options?.maxTokens ?? 4096,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || `Synthesis failed: ${response.status}`);
    }

    return response.json();
  },
};

// ── Expressr config ──

const expressrConfig: ExpressrUIConfig = {
  user: { id: 'daniel', name: 'Daniel Beer' },
  llm: llmProvider,
  autoSaveInterval: 30000,
  onSave: async (doc) => {
    console.log('[amplifyr] Saved:', doc.title, `(${doc.wordCount} words)`);
  },
  onPublish: async (doc, format) => {
    console.log('[amplifyr] Publish:', doc.title, format);
  },
};

// ── Tab Navigation ──

type AppView = 'chat' | 'documents' | 'governance' | 'reachr' | 'scheduling' | 'data' | 'apps';

// ── Documents View ──

function DocumentsContent() {
  return (
    <ReversrProvider maxHistory={100}>
      <ExpressrProvider config={expressrConfig}>
        <div style={{ height: '100%', width: '100%' }}>
          <ExpressrWorkspace config={expressrConfig} />
        </div>
      </ExpressrProvider>
    </ReversrProvider>
  );
}

// ── Main App ──

export function App() {
  const [view, setView] = useState<AppView>('documents');

  const tab = (id: AppView, label: string, gov = false) => {
    const active = view === id;
    const cls = ['amp-nav-tab', active && 'amp-nav-tab--active', gov && 'amp-nav-tab--gov']
      .filter(Boolean)
      .join(' ');
    return (
      <button className={cls} onClick={() => setView(id)}>
        {label}
      </button>
    );
  };

  return (
    <div className="amp-root">
      <div className="amp-topbar">
        <span className="amp-logo">AMPLIFYR</span>
        {tab('documents', 'Documents')}
        {tab('chat', '🔥 Ember')}
        {tab('reachr', 'Reachr')}
        {tab('scheduling', 'Scheduling')}
        {tab('data', 'Data')}
        {tab('apps', 'Apps')}
        {tab('governance', 'Governance', true)}
      </div>
      <div className="amp-content">
        {view === 'documents' ? (
          <DocumentsContent />
        ) : view === 'chat' ? (
          <ChatView />
        ) : view === 'reachr' ? (
          <ReachrView />
        ) : view === 'scheduling' ? (
          <SchedulingView />
        ) : view === 'data' ? (
          <DataView />
        ) : view === 'apps' ? (
          <AppsView />
        ) : (
          <GovernancePanel />
        )}
      </div>
    </div>
  );
}

export default App;
